import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Chinese mirror of the Thai apostille-vs-legalisation guide. */
const TITLE = "海牙认证与领事认证的区别 — 泰国文件逐国办理指南";
const DESC =
  "泰国文件出境使用如何认证：海牙附加证明书与领事认证的区别、正确的办理顺序，以及各主要国家的注意事项。";
const PATH = "/blog/apostille-vs-legalization-by-country";
const URL = `/zh${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "海牙认证（Apostille）与领事认证有什么实际区别？",
    answer:
      "海牙附加证明书是依《海牙取消认证公约》签发的单一证明，公约成员国无需再经使领馆办理即可接受。领事认证是较早的多层链条：先由出具机关认证，再由该国外交部门认证，最后由目的地国驻当地使领馆认证。区别在于步骤数量、时间与目的地是否为公约成员。",
  },
  {
    question: "泰国文件目前走哪一条路径？",
    answer:
      "实务上仍应先向目的地机关确认。多数情形下泰国文件的通行做法是：由公证律师认证私文书、再经外交部领事司认证，最后由目的地国使领馆办理领事认证。若目的地接受附加证明书路径，则可省去使领馆一步。请在开始前逐案确认。",
  },
  {
    question: "翻译应该在认证之前还是之后？",
    answer:
      "先取得完整、盖有印章与签署的原件，再进行翻译，且翻译必须包含印章、钢印与批注文字。若先翻译后认证，后加的认证不会出现在译文中，文件常因此被退回并须重做。",
  },
  {
    question: "领事司认证需要多久？",
    answer:
      "工作日数量视文件种类、是否需要事先核验、以及当期受理量而定。稳妥的做法是从递交截止日往前倒推，为每一环节预留时间，而不是从最早日期往后推算。",
  },
  {
    question: "复印件可以认证吗？",
    answer:
      "多数机关要求原件或由出具机关签发的认证副本。普通复印件通常不被接受，即使已经翻译。若原件无法离手，应先向出具机关申请可用于对外的认证副本。",
  },
  {
    question: "常见被退件的原因有哪些？",
    answer:
      "护照与泰文记录的姓名拼写不一致、译文遗漏印章文字、认证顺序颠倒、文件签发日期超出目的地的有效期窗口、以及授权委托书的权限范围不足以涵盖后续所有步骤。",
  },
  {
    question: "各国的要求会不同吗？",
    answer:
      "会。同一份泰国文件，澳大利亚可能要求 NAATI 译员、部分欧洲国家接受附加证明书、部分中东与亚洲国家仍要求完整领事认证链。请以受理机关最新公告为准。",
  },
];

export const Route = createFileRoute("/zh/blog/apostille-vs-legalization-by-country")({
  component: ZhApostilleVsLegalisation,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "zh" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("apostille-vs-legalization-by-country") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("apostille-vs-legalization-by-country", "zh") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("apostille-vs-legalization-by-country") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "泰国海牙认证, 泰国领事认证, 泰国文件认证流程, 泰国外交部认证, 泰国文件翻译认证",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "zh"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "首页", url: "/zh" },
              { name: "指南", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("apostille-vs-legalization-by-country"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Apostille & Legalisation",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const SEQUENCE: string[] = [
  "向目的地机关确认所需认证层级、可接受语言与有效期窗口",
  "取得原件或由出具机关签发的认证副本",
  "私文书先由公证律师认证签名",
  "依需要送外交部领事司认证",
  "完整翻译，含印章、钢印与批注文字",
  "办理目的地使领馆领事认证，或在可行时改走附加证明书路径",
];

const COUNTRIES: Array<{ c: string; note: string }> = [
  { c: "澳大利亚", note: "多数机关要求 NAATI 认证译员；部分文件另需领事司认证。" },
  { c: "美国", note: "视受理州与机关而定，常见为公证律师认证加使领馆或州务卿层级要求。" },
  { c: "中国内地", note: "通常要求完整认证链，译文须完整且与原件对应。" },
  { c: "日本 / 韩国", note: "多要求领事司认证后再由使领馆办理，姓名拼写须与护照一致。" },
  { c: "欧洲多国", note: "若目的地为公约成员且接受附加证明书，可省去使领馆一步。" },
  { c: "中东多国", note: "普遍要求完整领事认证链，并常另有翻译语言要求。" },
];

const MISTAKES: string[] = [
  "先翻译后认证，导致认证内容未出现在译文中",
  "使用普通复印件而非原件或认证副本",
  "同一档案内姓名拼写不一致",
  "文件签发过早，超出目的地有效期窗口",
  "授权委托书权限过窄，无法涵盖后续认证与递交",
  "译文遗漏印章、钢印与批注文字",
];

function ZhApostilleVsLegalisation() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/zh" className="text-muted-foreground hover:text-foreground">
          首页
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          指南
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="apostille-vs-legalization-by-country" locale="zh" />
      <p className="answer-summary speakable-summary">
        泰国文件被退回，往往不是内容有误，而是认证顺序错误、译文遗漏印章文字，或签发日期已超出目的地的有效期窗口。
        正确做法是从递交截止日往前倒推：先确认目的地要求，再依原件、公证、领事司、翻译、使领馆的顺序逐步完成。
      </p>

      <h2>1. 两条路径的差别</h2>
      <p>
        附加证明书（Apostille）是一份证明取代整条认证链，公约成员国之间互相承认；领事认证则须逐层办理，最后一步由目的地国使领馆完成。
        判断依据只有一个：受理文件的机关目前接受哪一种。请以该机关最新公告为准。
      </p>

      <h2>2. 正确的办理顺序</h2>
      <ol>
        {SEQUENCE.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. 各国注意事项</h2>
      <ul>
        {COUNTRIES.map((x) => (
          <li key={x.c}>
            <strong>{x.c}：</strong> {x.note}
          </li>
        ))}
      </ul>

      <h2>4. 常见退件原因</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>5. 常见问题</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>6. 参考来源与相关服务</h2>
      <p className="text-sm">
        参考：{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          泰国外交部领事司
        </a>{" "}
        ·{" "}
        <a href="https://www.hcch.net/en/instruments/conventions/specialised-sections/apostille" target="_blank" rel="noreferrer">
          HCCH 海牙认证公约
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/zh/lgz" className="text-primary underline">
            文件认证与领事认证服务
          </Link>
        </li>
        <li>
          <Link to="/zh/notary" className="text-primary underline">
            公证律师服务
          </Link>
        </li>
        <li>
          <Link to="/zh/naati" className="text-primary underline">
            NAATI 认证翻译
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        内容复核日期 {PUBLISHED}。本指南仅为规划用的一般信息，不代表任何机关的决定。办理前请向出具机关与目的地机关确认最新要求。
      </p>
    </article>
  );
}
